import { useEffect, useState } from "react";
import { Calendar, Plus, Check, X, Loader2, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GlassCard, PageHeader, EmptyState, StatusBadge } from "../components/shared";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const TYPES = ["sick", "casual", "paid", "unpaid"];

export default function Leaves() {
    const { user } = useAuth();
    const [me, setMe] = useState({ items: [], balance: {}, used: {} });
    const [all, setAll] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [open, setOpen] = useState(false);
    const [adminOpen, setAdminOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const canApprove = ["super_admin", "hr", "manager"].includes(user?.role);
    const canManage = ["super_admin", "hr"].includes(user?.role);

    const todayStr = new Date().toISOString().split("T")[0];
    const todayOnLeave = all.filter(leave => {
        if (leave.status !== "approved") return false;
        const start = leave.start_date.split("T")[0];
        const end = leave.end_date.split("T")[0];
        return todayStr >= start && todayStr <= end;
    });

    const load = async () => {
        setLoading(true);
        try {
            const reqs = [api.get("/leaves/me")];
            if (canApprove) reqs.push(api.get("/leaves/all"));
            if (canManage) reqs.push(api.get("/employees?limit=500&status=active"));
            const [m, a, emp] = await Promise.all(reqs);
            setMe(m.data);
            if (a) setAll(a.data.items || []);
            if (emp) setEmployees(emp.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-line */ }, []);

    const act = async (id, status) => {
        try {
            const { data } = await api.post(`/leaves/${id}/action`, { status });
            if (data.email_status && data.email_status.status === "error") {
                toast.error(`Leave ${status}. Email failed: ${data.email_status.error}`, { duration: 8050 });
                console.error("Email Delivery Error Log:", data.email_status.error);
            } else if (data.email_status && data.email_status.status === "sent") {
                toast.success(`Leave ${status}. Email sent to employee!`);
            } else {
                toast.success(`Leave ${status}`);
            }
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this leave request?")) return;
        try {
            await api.delete(`/leaves/${id}`);
            toast.success("Leave deleted");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div>
            <PageHeader
                title="Leaves"
                subtitle="Apply for time off and track approvals."
                testId="leaves-header"
                actions={
                    <>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button className="rounded-full" data-testid="apply-leave-btn"><Plus className="w-4 h-4 mr-1" /> Apply leave</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
                                <ApplyForm onSubmitted={() => { setOpen(false); load(); }} />
                            </DialogContent>
                        </Dialog>
                        {canManage && (
                            <Dialog open={adminOpen} onOpenChange={(o) => { setAdminOpen(o); if (!o) setEditing(null); }}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="rounded-full" data-testid="admin-leave-btn"><Plus className="w-4 h-4 mr-1" /> Add for employee</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>{editing ? "Edit leave" : "Add employee leave"}</DialogTitle></DialogHeader>
                                    <AdminLeaveForm employees={employees} leave={editing} onSubmitted={() => { setAdminOpen(false); setEditing(null); load(); }} />
                                </DialogContent>
                            </Dialog>
                        )}
                    </>
                }
            />

            {/* Balances */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {["paid", "sick", "casual"].map((t) => {
                    const total = me.balance?.[t] || 0;
                    const used = me.used?.[t] || 0;
                    const remaining = Math.max(total - used, 0);
                    return (
                        <GlassCard key={t} className="p-5">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground capitalize">{t} leaves</div>
                            <div className="font-heading text-3xl font-bold mt-1">{remaining}<span className="text-sm text-muted-foreground"> / {total}</span></div>
                            <div className="text-xs text-muted-foreground mt-1">{used} used</div>
                        </GlassCard>
                    );
                })}
                <GlassCard className="p-5">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Unpaid this year</div>
                    <div className="font-heading text-3xl font-bold mt-1">{me.used?.unpaid || 0}</div>
                </GlassCard>
            </div>

            <Tabs defaultValue="mine">
                <TabsList>
                    <TabsTrigger value="mine" data-testid="tab-my-leaves">My leaves</TabsTrigger>
                    {canApprove && <TabsTrigger value="all" data-testid="tab-all-leaves">All requests</TabsTrigger>}
                    {canApprove && <TabsTrigger value="today-on-leave" data-testid="tab-today-on-leave">Today on Leave</TabsTrigger>}
                </TabsList>
                <TabsContent value="mine" className="mt-4">
                    <LeavesTable items={me.items} loading={loading} />
                </TabsContent>
                {canApprove && (
                    <TabsContent value="all" className="mt-4">
                        <LeavesTable
                            items={all}
                            loading={loading}
                            canAct
                            canManage={canManage}
                            onAct={act}
                            onEdit={(leave) => { setEditing(leave); setAdminOpen(true); }}
                            onDelete={remove}
                        />
                    </TabsContent>
                )}
                {canApprove && (
                    <TabsContent value="today-on-leave" className="mt-4">
                        <GlassCard className="p-6">
                            <h3 className="font-heading font-bold text-base mb-4 text-slate-800 dark:text-slate-100">Today on Leave</h3>
                            {todayOnLeave.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-6">No employees are on leave today.</p>
                            ) : (
                                <div className="divide-y divide-slate-150 dark:divide-slate-800">
                                    {todayOnLeave.map((leave) => (
                                        <div key={leave.id} className="py-3 flex justify-between items-center text-xs">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400 flex items-center justify-center font-bold">
                                                    {leave.user_name?.[0]?.toUpperCase() || "L"}
                                                </div>
                                                <div>
                                                    <span className="font-semibold block text-slate-800 dark:text-slate-200">{leave.user_name}</span>
                                                    <span className="text-[10px] text-muted-foreground uppercase">{leave.leave_type} Leave</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="font-medium text-slate-600 dark:text-slate-400">{leave.start_date} to {leave.end_date}</span>
                                                <span className="block text-[9px] text-muted-foreground">{leave.days} days ({leave.reason})</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </GlassCard>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function AdminLeaveForm({ employees, leave, onSubmitted }) {
    const [form, setForm] = useState({
        user_id: leave?.user_id || employees[0]?.id || "",
        leave_type: leave?.leave_type || "casual",
        start_date: leave?.start_date || "",
        end_date: leave?.end_date || "",
        reason: leave?.reason || "",
        status: leave?.status || "pending",
        comment: leave?.comment || "",
    });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (leave) {
                await api.patch(`/leaves/${leave.id}`, form);
                toast.success("Leave updated");
            } else {
                await api.post("/leaves/admin", form);
                toast.success("Leave added");
            }
            onSubmitted();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            {!leave && (
                <div>
                    <Label>Employee</Label>
                    <select required value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>)}
                    </select>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                    <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div><Label>Status</Label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm">
                        {["pending", "approved", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
                <div><Label>End</Label><Input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" /></div>
            <div><Label>Comment</Label><Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="mt-1" /></div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading || (!leave && !form.user_id)}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save leave"}
            </Button>
        </form>
    );
}

function ApplyForm({ onSubmitted }) {
    const [form, setForm] = useState({ leave_type: "casual", start_date: "", end_date: "", reason: "" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/leaves/apply", form);
            toast.success("Leave applied");
            onSubmitted();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>Type</Label>
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="leave-type">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="leave-start" /></div>
                <div><Label>End</Label><Input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="leave-end" /></div>
            </div>
            <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" data-testid="leave-reason" /></div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="leave-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
            </Button>
        </form>
    );
}

function LeavesTable({ items, loading, canAct, canManage, onAct, onEdit, onDelete }) {
    if (loading) return <div className="text-center py-6 text-sm text-muted-foreground">Loading…</div>;
    if (!items?.length) return <GlassCard><EmptyState icon={Calendar} title="No leaves" description="Apply for leave to see records." /></GlassCard>;
    return (
        <GlassCard className="overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-black/[0.03] dark:bg-white/[0.03]">
                        <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            {canAct && <th className="text-left p-3">Employee</th>}
                            <th className="text-left p-3">Type</th>
                            <th className="text-left p-3">From</th>
                            <th className="text-left p-3">To</th>
                            <th className="text-left p-3">Days</th>
                            <th className="text-left p-3">Reason</th>
                            <th className="text-left p-3">Status</th>
                            {canAct && <th className="text-right p-3">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((l) => (
                            <tr key={l.id} className="border-t border-black/5 dark:border-white/5">
                                {canAct && <td className="p-3">{l.user_name}</td>}
                                <td className="p-3 capitalize">{l.leave_type}</td>
                                <td className="p-3">{l.start_date}</td>
                                <td className="p-3">{l.end_date}</td>
                                <td className="p-3">{l.days}</td>
                                <td className="p-3 max-w-[260px] truncate">{l.reason || "—"}</td>
                                <td className="p-3"><StatusBadge status={l.status} /></td>
                                {canAct && (
                                    <td className="p-3 text-right">
                                        <div className="flex gap-1 justify-end">
                                            {l.status === "pending" && (
                                                <>
                                                <button onClick={() => onAct(l.id, "approved")} className="p-1.5 rounded-lg hover:bg-success/10 text-success" data-testid={`approve-${l.id}`}><Check className="w-4 h-4" /></button>
                                                <button onClick={() => onAct(l.id, "rejected")} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`reject-${l.id}`}><X className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            {canManage && (
                                                <>
                                                    <button onClick={() => onEdit(l)} className="p-1.5 rounded-lg hover:bg-primary/10 text-primary" data-testid={`edit-leave-${l.id}`}><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => onDelete(l.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`delete-leave-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </GlassCard>
    );
}
